import { useState, useEffect } from "react";
import { 
  Search, 
  BarChart3, 
  TrendingUp,
  TrendingDown,
  Loader2,
  Filter,
  Play,
  RefreshCw,
  Target,
  Percent,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Sparkles,
  LineChart
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import { LineChart as ReLineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuantTools = () => {
  const [loading, setLoading] = useState(true);
  const [presets, setPresets] = useState([]);
  const [screenResults, setScreenResults] = useState(null);
  const [screening, setScreening] = useState(false);
  const [backtestResults, setBacktestResults] = useState(null);
  const [backtesting, setBacktesting] = useState(false);
  
  // Screener form
  const [screenerForm, setScreenerForm] = useState({
    maxOcf: 0.75,
    minDividendYield: 0,
    minMomentum: 0
  });
  
  // Backtest form
  const [backtestForm, setBacktestForm] = useState({
    initialInvestment: 10000,
    years: 5,
    simulations: 500,
    portfolio: [
      { symbol: "VUSA.L", weight: 40, name: "Vanguard S&P 500" },
      { symbol: "VWRL.L", weight: 30, name: "Vanguard All-World" },
      { symbol: "VGOV.L", weight: 30, name: "UK Gilts" }
    ]
  });

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    try {
      const response = await axios.get(`${API}/screen/presets`);
      setPresets(response.data.presets || []);
    } catch (error) {
      console.error("Error fetching presets:", error);
    } finally {
      setLoading(false);
    }
  };

  const runScreener = async (criteria = null) => {
    setScreening(true);
    try {
      const payload = criteria || {
        max_ocf: screenerForm.maxOcf,
        min_dividend_yield: screenerForm.minDividendYield > 0 ? screenerForm.minDividendYield : null,
        min_momentum: screenerForm.minMomentum > 0 ? screenerForm.minMomentum : null
      };
      
      const response = await axios.post(`${API}/screen`, payload);
      setScreenResults(response.data);
      toast.success(`Found ${response.data.matches_found} matching funds`);
    } catch (error) {
      toast.error("Screening failed");
    } finally {
      setScreening(false);
    }
  };

  const runBacktest = async () => {
    setBacktesting(true);
    setBacktestResults(null);
    try {
      const response = await axios.post(`${API}/backtest`, {
        portfolio: backtestForm.portfolio.map(h => ({ symbol: h.symbol, weight: h.weight })),
        initial_investment: backtestForm.initialInvestment,
        years: backtestForm.years,
        simulations: backtestForm.simulations
      });
      setBacktestResults(response.data);
      toast.success("Backtest complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Backtest failed");
    } finally {
      setBacktesting(false);
    }
  };

  const applyPreset = (preset) => {
    runScreener(preset.criteria);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="quant-tools-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" />
            Quantitative Tools
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Fund screener and portfolio backtesting with Monte Carlo simulations
          </p>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="screener" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="screener" data-testid="tab-screener">
            <Search className="w-4 h-4 mr-1" /> Fund Screener
          </TabsTrigger>
          <TabsTrigger value="backtest" data-testid="tab-backtest">
            <LineChart className="w-4 h-4 mr-1" /> Backtester
          </TabsTrigger>
        </TabsList>

        {/* Screener Tab */}
        <TabsContent value="screener" className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            {/* Screener Controls */}
            <Card className="md:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg">Filter Criteria</CardTitle>
                <CardDescription>Set your fund screening parameters</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Max OCF */}
                <div className="space-y-2">
                  <Label>Max OCF: {screenerForm.maxOcf}%</Label>
                  <Slider
                    value={[screenerForm.maxOcf]}
                    onValueChange={([v]) => setScreenerForm(prev => ({ ...prev, maxOcf: v }))}
                    min={0.1}
                    max={2}
                    step={0.05}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Cheap (0.1%)</span>
                    <span>Expensive (2%)</span>
                  </div>
                </div>

                {/* Min Dividend Yield */}
                <div className="space-y-2">
                  <Label>Min Dividend Yield: {screenerForm.minDividendYield}%</Label>
                  <Slider
                    value={[screenerForm.minDividendYield]}
                    onValueChange={([v]) => setScreenerForm(prev => ({ ...prev, minDividendYield: v }))}
                    min={0}
                    max={8}
                    step={0.5}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Any</span>
                    <span>High (8%)</span>
                  </div>
                </div>

                {/* Min Momentum */}
                <div className="space-y-2">
                  <Label>Min 6M Momentum: {screenerForm.minMomentum}%</Label>
                  <Slider
                    value={[screenerForm.minMomentum]}
                    onValueChange={([v]) => setScreenerForm(prev => ({ ...prev, minMomentum: v }))}
                    min={0}
                    max={30}
                    step={1}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Any</span>
                    <span>Strong (30%)</span>
                  </div>
                </div>

                <Button onClick={() => runScreener()} disabled={screening} className="w-full">
                  {screening ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                  Run Screen
                </Button>

                {/* Quick Presets */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Quick Presets</Label>
                  <div className="grid gap-2">
                    {presets.map((preset, i) => (
                      <Button
                        key={i}
                        variant="outline"
                        size="sm"
                        className="justify-start text-xs"
                        onClick={() => applyPreset(preset)}
                      >
                        <Sparkles className="w-3 h-3 mr-1" />
                        {preset.name}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Screen Results</CardTitle>
                <CardDescription>
                  {screenResults 
                    ? `Found ${screenResults.matches_found} of ${screenResults.total_funds_screened} funds`
                    : "Run a screen to see matching funds"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {screenResults ? (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {screenResults.results?.map((fund, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold">
                            {i + 1}
                          </div>
                          <div>
                            <p className="font-medium">{fund.symbol}</p>
                            <p className="text-xs text-muted-foreground">{fund.name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs">
                          <div className="text-center">
                            <p className="text-muted-foreground">OCF</p>
                            <p className="font-semibold">{fund.ocf}%</p>
                          </div>
                          <div className="text-center">
                            <p className="text-muted-foreground">Yield</p>
                            <p className="font-semibold">{fund.dividend_yield}%</p>
                          </div>
                          <div className="text-center">
                            <p className="text-muted-foreground">6M</p>
                            <p className={`font-semibold ${fund.momentum_6m >= 0 ? "text-success" : "text-destructive"}`}>
                              {fund.momentum_6m >= 0 ? "+" : ""}{fund.momentum_6m}%
                            </p>
                          </div>
                          <Badge variant="outline">{fund.sector}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 text-muted-foreground">
                    <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">No screen results yet</p>
                    <p className="text-sm mt-1">Set your criteria and click "Run Screen"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Backtest Tab */}
        <TabsContent value="backtest" className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            {/* Backtest Controls */}
            <Card className="md:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg">Backtest Settings</CardTitle>
                <CardDescription>Configure your simulation parameters</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Initial Investment */}
                <div className="space-y-2">
                  <Label>Initial Investment</Label>
                  <Input
                    type="number"
                    value={backtestForm.initialInvestment}
                    onChange={(e) => setBacktestForm(prev => ({ ...prev, initialInvestment: parseInt(e.target.value) || 10000 }))}
                    min={1000}
                    max={1000000}
                  />
                </div>

                {/* Years */}
                <div className="space-y-2">
                  <Label>Investment Period: {backtestForm.years} years</Label>
                  <Slider
                    value={[backtestForm.years]}
                    onValueChange={([v]) => setBacktestForm(prev => ({ ...prev, years: v }))}
                    min={1}
                    max={20}
                    step={1}
                  />
                </div>

                {/* Simulations */}
                <div className="space-y-2">
                  <Label>Simulations: {backtestForm.simulations}</Label>
                  <Slider
                    value={[backtestForm.simulations]}
                    onValueChange={([v]) => setBacktestForm(prev => ({ ...prev, simulations: v }))}
                    min={100}
                    max={2000}
                    step={100}
                  />
                </div>

                {/* Portfolio */}
                <div className="space-y-2">
                  <Label>Test Portfolio</Label>
                  <div className="space-y-2 text-xs">
                    {backtestForm.portfolio.map((h, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <span>{h.symbol}</span>
                        <Badge variant="outline">{h.weight}%</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <Button onClick={runBacktest} disabled={backtesting} className="w-full">
                  {backtesting ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Running...</>
                  ) : (
                    <><Play className="w-4 h-4 mr-1" /> Run Backtest</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Simulation Results</CardTitle>
                <CardDescription>
                  {backtestResults 
                    ? `Based on ${backtestResults.input?.simulations} Monte Carlo simulations`
                    : "Run a backtest to see projections"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {backtestResults ? (
                  <div className="space-y-6">
                    {/* Key Metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-primary/10 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground mb-1">Median Final Value</p>
                        <p className="text-xl font-bold">£{backtestResults.simulation_results?.median_final_value?.toLocaleString()}</p>
                      </div>
                      <div className="p-4 bg-success/10 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground mb-1">Expected CAGR</p>
                        <p className="text-xl font-bold text-success">{backtestResults.simulation_results?.expected_cagr}%</p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground mb-1">Prob. of Profit</p>
                        <p className="text-xl font-bold">{backtestResults.simulation_results?.probability_profit}%</p>
                      </div>
                      <div className="p-4 bg-warning/10 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground mb-1">Sharpe Ratio</p>
                        <p className="text-xl font-bold">{backtestResults.portfolio_stats?.sharpe_ratio}</p>
                      </div>
                    </div>

                    {/* Percentile Distribution */}
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm">Outcome Distribution</h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-20 text-muted-foreground">Best Case</span>
                          <Progress value={100} className="flex-1 h-2" />
                          <span className="text-xs font-medium w-24 text-right">£{backtestResults.simulation_results?.best_case?.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-20 text-muted-foreground">95th %ile</span>
                          <Progress value={95} className="flex-1 h-2" />
                          <span className="text-xs font-medium w-24 text-right">£{backtestResults.simulation_results?.percentiles?.p95?.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-20 text-muted-foreground">75th %ile</span>
                          <Progress value={75} className="flex-1 h-2" />
                          <span className="text-xs font-medium w-24 text-right">£{backtestResults.simulation_results?.percentiles?.p75?.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-20 text-primary font-semibold">Median</span>
                          <Progress value={50} className="flex-1 h-2 bg-primary/20" />
                          <span className="text-xs font-bold w-24 text-right text-primary">£{backtestResults.simulation_results?.percentiles?.p50?.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-20 text-muted-foreground">25th %ile</span>
                          <Progress value={25} className="flex-1 h-2" />
                          <span className="text-xs font-medium w-24 text-right">£{backtestResults.simulation_results?.percentiles?.p25?.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-20 text-muted-foreground">5th %ile</span>
                          <Progress value={5} className="flex-1 h-2" />
                          <span className="text-xs font-medium w-24 text-right">£{backtestResults.simulation_results?.percentiles?.p5?.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-20 text-destructive">Worst Case</span>
                          <Progress value={1} className="flex-1 h-2 bg-destructive/20" />
                          <span className="text-xs font-medium w-24 text-right text-destructive">£{backtestResults.simulation_results?.worst_case?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Sample Paths Chart */}
                    {backtestResults.paths_sample && backtestResults.paths_sample.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sm">Sample Simulation Paths</h4>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <ReLineChart>
                              <XAxis 
                                dataKey="month" 
                                tick={{ fontSize: 10 }}
                                tickFormatter={(v, i) => i === 0 ? "Start" : `Y${Math.floor(i / 12) + 1}`}
                              />
                              <YAxis 
                                tick={{ fontSize: 10 }}
                                tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`}
                              />
                              <Tooltip 
                                formatter={(v) => [`£${v?.toLocaleString()}`, "Value"]}
                              />
                              {backtestResults.paths_sample.slice(0, 5).map((path, i) => (
                                <Line
                                  key={i}
                                  data={path.map((v, month) => ({ month, value: v }))}
                                  dataKey="value"
                                  stroke={i === 0 ? "#22c55e" : i === 1 ? "#3b82f6" : i === 2 ? "#f59e0b" : "#94a3b8"}
                                  strokeWidth={1}
                                  dot={false}
                                />
                              ))}
                            </ReLineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Risk Metrics */}
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-success/10 rounded-lg">
                        <CheckCircle className="w-5 h-5 mx-auto mb-1 text-success" />
                        <p className="text-xs text-muted-foreground">Prob. Double Money</p>
                        <p className="font-bold">{backtestResults.simulation_results?.probability_double}%</p>
                      </div>
                      <div className="p-3 bg-destructive/10 rounded-lg">
                        <AlertTriangle className="w-5 h-5 mx-auto mb-1 text-destructive" />
                        <p className="text-xs text-muted-foreground">Prob. Lose 20%+</p>
                        <p className="font-bold">{backtestResults.simulation_results?.probability_lose_20pct}%</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <TrendingUp className="w-5 h-5 mx-auto mb-1" />
                        <p className="text-xs text-muted-foreground">Expected Volatility</p>
                        <p className="font-bold">{backtestResults.portfolio_stats?.expected_volatility}%</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-16 text-muted-foreground">
                    <LineChart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">No backtest results yet</p>
                    <p className="text-sm mt-1">Configure settings and click "Run Backtest"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default QuantTools;
